import React, { useRef } from 'react';
import { Printer, ArrowLeft, Menu } from 'lucide-react';
import { useSidebar } from '../layout/Layout';
import './print.css';

const CatalogoBase = ({ 
  titulo, 
  subtitulo, 
  children, 
  onVoltar, 
  logoOrganizador, 
  bannerLeilao,
  landscape = false,
  showHeaderInfo = true,
  leiloes = [],
  selectedLeilao = '',
  onLeilaoChange = null,
  extraButtons = null,
  printStyleOverrides = '',
}) => {
  const { setSidebarOpen } = useSidebar();
  const printRef = useRef();

  const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:8001/').replace(/\/$/, '');
  const logoSrc = logoOrganizador
    ? (logoOrganizador.startsWith('http') ? logoOrganizador : `${apiBase}/${logoOrganizador.replace(/^\//, '')}`)
    : null;

  const handlePrint = () => {
    if (printRef.current) {
      // Mostrar loading
      const button = document.querySelector('button');
      const originalText = button?.textContent;
      if (button) {
        button.textContent = 'Preparando...';
        button.disabled = true;
      }
      
      setTimeout(() => {
        // Converter src relativos de imagens para absolutos antes de copiar o innerHTML
        const cloneNode = printRef.current.cloneNode(true);
        cloneNode.querySelectorAll('img').forEach(img => {
          if (img.src && !img.src.startsWith('data:')) {
            img.src = img.src; // já é absoluto no DOM
          }
        });

        const printWindow = window.open('', '_blank', 'width=800,height=600');
        printWindow.document.write(`
          <html>
            <head>
              <title>Catálogo - ${titulo}</title>
              <style>
                body { 
                  font-family: Arial, sans-serif; 
                  margin: 0; 
                  padding: 15px;
                  background: white;
                }
                
                /* Cabeçalho com logos */
                .header-logos {
                  display: table;
                  width: 100%;
                  margin-bottom: 10pt;
                  padding-bottom: 5pt;
                }
                
                .logo-left, .header-center, .logo-right {
                  display: table-cell;
                  vertical-align: middle;
                }
                
                .logo-left {
                  width: 25%;
                  text-align: left;
                }
                
                .logo-right {
                  width: 25%;
                  text-align: right;
                }
                
                .header-center {
                  width: 50%;
                  text-align: center;
                  padding: 0 20px;
                }
                
                .logo-left img {
                  max-width: 180px;
                  max-height: 100px;
                  object-fit: contain;
                }
                
                .logo-right img {
                  max-width: 100px;
                  max-height: 70px;
                  object-fit: contain;
                }
                
                h1 { 
                  font-size: 18pt; 
                  margin: 4pt 0; 
                  font-weight: bold;
                  color: #000;
                }
                
                h2 { 
                  font-size: 12pt; 
                  margin: 2pt 0;
                  color: #333;
                }
                
                table { 
                  width: 100%; 
                  border-collapse: collapse; 
                  margin: 10pt 0; 
                  font-size: 10pt;
                }
                
                th { 
                  background-color: #e8e8e8; 
                  padding: 5pt; 
                  border: 1.5pt solid #000; 
                  font-weight: bold; 
                  text-align: center; 
                  font-size: 9pt;
                }
                
                td { 
                  padding: 4pt; 
                  border: 1pt solid #333; 
                  font-size: 10pt;
                  text-transform: uppercase;
                  white-space: nowrap;
                }
                
                /* Observações com fonte responsiva */
                .observacoes-col {
                  font-size: 7pt;
                  padding: 2pt;
                  max-width: 120px;
                  word-wrap: break-word;
                  white-space: normal;
                }
                
                td:first-child {
                  font-weight: bold;
                }
                
                .totals-section {
                  margin-top: 15pt;
                  text-align: center;
                  font-size: 11pt;
                  font-weight: bold;
                  page-break-inside: avoid;
                }
                
                .totals-grid {
                  display: flex;
                  justify-content: center;
                  gap: 20pt;
                  flex-wrap: nowrap;
                }
                
                .total-item {
                  display: inline-block;
                  padding: 5pt 10pt;
                  background: #f5f5f5;
                  border-radius: 3pt;
                  min-width: 70pt;
                  text-align: center;
                }
                
                .total-number {
                  font-size: 14pt;
                  font-weight: bold;
                  display: block;
                }
                
                .total-label {
                  font-size: 9pt;
                  color: #666;
                  display: block;
                }
                
                @page {
                  size: A4;
                  margin: 0.2cm;
                }
                
                .footer-final {
                  margin-top: 20pt;
                  text-align: center;
                  font-size: 9pt;
                  color: #333;
                  font-weight: bold;
                  border-top: 2pt solid #000;
                  padding-top: 8pt;
                  page-break-inside: avoid;
                }
                
                @media print { 
                  .page-break-after { 
                    break-after: page;
                    page-break-after: always;
                  }
                  
                  .print-footer {
                    display: none;
                  }
                }

                ${printStyleOverrides}
              </style>
            </head>
            <body>
              ${cloneNode.innerHTML}

            </body>
          </html>
        `);
        
        printWindow.document.close();
        
        // Aguardar carregamento das imagens
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            
            // Forçar fechamento da janela após impressão
            const forceClose = () => {
              try {
                printWindow.close();
              } catch (e) {
                console.log('Janela já fechada');
              }
            };
            
            // Múltiplas tentativas de fechamento
            setTimeout(forceClose, 500);
            setTimeout(forceClose, 1000);
            setTimeout(forceClose, 2000);
            
            // Listener para detectar quando a impressão termina
            printWindow.addEventListener('afterprint', forceClose);
            printWindow.addEventListener('beforeunload', forceClose);
          }, 500);
        };
        
        // Restaurar botão
        if (button) {
          button.textContent = originalText;
          button.disabled = false;
        }
      }, 300);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* Header - oculto na impressão */}
        <div className="no-print bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between mb-4 md:hidden">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-xl text-white hover:bg-white/20 transition-all duration-200"
            >
              <Menu className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-semibold text-white truncate">{titulo}</h1>
            <div className="w-10"></div>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
            <div className="min-w-0 flex-1">
              {onVoltar && (
                <div className="flex items-center gap-3 mb-2">
                  <button
                    onClick={onVoltar}
                    className="flex items-center gap-2 text-blue-100 hover:text-white transition-colors"
                  >
                    <ArrowLeft size={20} />
                    <span className="hidden sm:inline">Voltar</span>
                  </button>
                </div>
              )}
              <h1 className="text-xl sm:text-3xl font-bold text-white hidden md:block">{titulo}</h1>
              {showHeaderInfo && subtitulo && (
                <p className="text-blue-100 mt-1 text-sm sm:text-base hidden sm:block">{subtitulo}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-3 items-center w-full sm:w-auto">
              {onLeilaoChange && leiloes.length > 0 && (
                <select
                  value={selectedLeilao}
                  onChange={(e) => onLeilaoChange(e.target.value)}
                  className="bg-white border border-gray-200 text-blue-600 px-3 py-2 rounded-xl text-sm font-medium w-full sm:w-auto sm:min-w-[200px] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {leiloes.map(leilao => (
                    <option key={leilao.id} value={leilao.id}>
                      {leilao.nome}
                    </option>
                  ))}
                </select>
              )}
              {extraButtons}
            </div>
          </div>
        </div>

        {/* Conteúdo do Relatório */}
        <div ref={printRef} className="print-content bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          {/* Cabeçalho do Relatório */}
          {(logoSrc || bannerLeilao || titulo) && (
            <div className="mb-6 print:mb-4">
              <div className="header-logos">
                <div className="logo-left">
                  {logoSrc && (
                    <img src={logoSrc} alt="Logo Organizador" className="max-w-full max-h-full object-contain" />
                  )}
                </div>
                <div className="header-center">
                  <h1 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2 print:text-3xl">{titulo}</h1>
                  {subtitulo && (
                    <h2 className="text-base sm:text-xl font-semibold text-gray-700 mb-1 print:text-xl">{subtitulo}</h2>
                  )}
                </div>
                <div className="logo-right">
                  {bannerLeilao ? (
                    <img src={bannerLeilao} alt="Banner Leilão" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <img src="/assets/rematei.png" alt="Rematei" className="max-w-full max-h-full object-contain" />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Conteúdo do Relatório */}
          {children}
        </div>


      </div>
    </div>
  );
};

export default CatalogoBase;